import { Formik, Form } from 'formik';
import Quill from 'quill';
import { useCallback, useEffect, useMemo, useRef } from 'react';

import { Button } from '@hivebrite/design-system/Button';

import ModalWithTemplate from 'hb-react/shared/components/DesignSystem/Modal/ModalWithTemplate';
import { useQuillEditorContext } from 'hb-react/shared/components/DesignSystem/QuillEditor/contexts/QuillEditorContext';
import InlineImageBlot from 'hb-react/shared/components/DesignSystem/QuillEditor/formats/InlineImageBlot';
import ErrorFocus from 'hb-react/shared/components/formik/components/ErrorFocus';
import { Text } from 'hb-react/shared/components/formik/components/inputs';
import RequiredExplanation from 'hb-react/shared/components/formik/components/RequiredExplanation';
import { getEmailRegex } from 'hb-react/shared/constants/emailRegex';
import { getUrlRegex } from 'hb-react/shared/utils/validators/urlFormat';

const validUrlRegex = getUrlRegex({ protocol: 'optional' });
const validEmailRegex = getEmailRegex({ mailto: 'optional' });

const ImageBlot = Quill.import('formats/image');

const AddLinkModal = ({ isOpen, onClose }) => {
  const { quillRef, setDisplayLinkModal, onAddLink, format } =
    useQuillEditorContext();

  // Store the "original" selection in a ref. As the modal opens the editor will lose
  // focus and we will lose the original selection so we need to store it.
  const selectionRef = useRef(quillRef.current?.getSelection());
  const selection = selectionRef.current;

  const searchInput = useRef();

  const { selectionIndex, selectionLength, initialValues, isImage } =
    useMemo(() => {
      // User is not focusing the editor at all, insert the link at the end.
      if (!selection) {
        return {
          selectionIndex: null,
          selectionLength: null,
          initialValues: {
            text: '',
            url: '',
          },
        };
      }

      const isTextSelectedByUser = selection?.length > 0;
      const userIsFocusingExistingLink = !!format?.link;

      // User is updating an inline-image
      const [blot] = quillRef.current.getLeaf(selection.index);
      const isImage = blot instanceof ImageBlot;

      // User is focusing an inline-image
      if (isImage) {
        return {
          selectionIndex: selection.index,
          selectionLength: selection.length,
          isImage: true,
          initialValues: {
            text: null,
            url: blot.getUrl?.(),
          },
        };
      }

      // User is updating existing link
      if (userIsFocusingExistingLink) {
        if (isTextSelectedByUser) {
          // User select a part of existing link, only take this part

          return {
            selectionIndex: selection.index,
            selectionLength: selection.length,
            initialValues: {
              text:
                quillRef.current.getText(selection.index, selection.length) ||
                '',
              url: format?.link || '',
            },
          };
        }

        // User is only focusing inside an existing link but not selecting it, take entire link
        const index = blot ? quillRef.current.getIndex(blot) : selection.index;
        const length = blot ? blot.length() : selection.length;

        return {
          selectionIndex: index,
          selectionLength: length,
          initialValues: {
            text: quillRef.current.getText(index, length) || '',
            url: format?.link || '',
          },
        };
      }

      // User is creating a new link
      if (isTextSelectedByUser) {
        // Prefill text with selected value

        return {
          selectionIndex: selection.index,
          selectionLength: selection.length,
          initialValues: {
            text:
              quillRef.current.getText(selection.index, selection.length) || '',
            url: '',
          },
        };
      }

      // Empty link with no text
      return {
        selectionIndex: selection.index,
        selectionLength: selection.length,
        initialValues: {
          text: '',
          url: '',
        },
      };
    }, [selection, format?.link, quillRef]);

  const handleSubmit = useCallback(
    (values) => {
      const { url, text } = values;
      let formattedUrl = url;

      if (validUrlRegex.test(url)) {
        formattedUrl = `https://${url.replace(/https?:\/\//, '')}`;
      }

      if (validEmailRegex.test(url)) {
        formattedUrl = `mailto:${url.replace('mailto:', '')}`;
      }

      quillRef.current.focus();

      const lastIndex = quillRef.current.getLength();

      // User is updating an inline-image
      const [blot] = quillRef.current.getLeaf(selectionIndex);
      const isImage = blot instanceof ImageBlot;

      if (isImage) {
        const isInlineImage = blot instanceof InlineImageBlot;
        let value;

        if (isInlineImage) {
          value = blot.value()['inline-image'];
        } else {
          // Convert ImageBlot to InlineImageBlot
          value = {
            src: blot.value().image,
          };
        }

        // NOTE: We blur to ensure the ImageResize controls are not being displayed
        // as they leave artefacts in the content during the replace otherwise
        quillRef.current.blur();
        blot.replaceWith(
          'inline-image',
          { ...value, link: formattedUrl },
          'user',
        );
        quillRef.current.focus();
      } else if (selectionIndex || selectionLength) {
        if (selectionLength === 0) {
          // No text selected, only cursor position
          quillRef.current.insertText(
            selectionIndex,
            text,
            'link',
            formattedUrl,
            'user',
          );
        } else {
          // Text selected, replace it with new link values
          quillRef.current.deleteText(selectionIndex, selectionLength, 'user');

          quillRef.current.insertText(
            selectionIndex,
            text,
            'link',
            formattedUrl,
            'user',
          );
        }
      } else {
        // Insert at the end if no focus
        quillRef.current.insertText(
          lastIndex - 1,
          text,
          'link',
          formattedUrl,
          'user',
        );
      }

      onAddLink && onAddLink(formattedUrl);

      setDisplayLinkModal(false);
    },
    [onAddLink, quillRef, selectionIndex, selectionLength, setDisplayLinkModal],
  );

  useEffect(() => {
    // Delay for searchInput ref
    setTimeout(() => {
      searchInput?.current?.focus();
    }, 100);
  }, [searchInput]);

  return (
    <Formik initialValues={initialValues} onSubmit={handleSubmit}>
      {({ handleSubmit, values: { url } }) => (
        <ModalWithTemplate
          footer={
            <>
              <Button variant="secondary" onClick={onClose}>
                {__('Cancel')}
              </Button>
              <Button onClick={handleSubmit} type="submit" ml={3}>
                {initialValues.url && !url && isImage
                  ? s__('QuillEditor|Remove link')
                  : s__('QuillEditor|Insert')}
              </Button>
            </>
          }
          header={s__('QuillEditor|Insert link')}
          isOpen={isOpen}
          isSticky
          onCloseModal={onClose}
          pb={0}
          screenReadersCloseButtonText={s__(
            'QuillEditorAccessibility|Close add a link window',
          )}
          width="sm"
        >
          <Form style={{ width: '100%' }}>
            <RequiredExplanation mt={0} mb={4} />

            <Text
              ref={searchInput}
              name="url"
              label={s__('QuillEditor|URL (https://www.example.com)')}
              placeholder={s__('QuillEditor|URL (https://www.example.com)')}
              validate={{
                required: !isImage, // For InlineImages allow returning an empty string to "clear" the link
                urlAndMailToFormat: true,
              }}
            />
            {!isImage && (
              <Text
                name="text"
                label={s__('QuillEditor|Text')}
                placeholder={s__('QuillEditor|Click here')}
                validate={{
                  required: true,
                }}
              />
            )}

            <ErrorFocus />
          </Form>
        </ModalWithTemplate>
      )}
    </Formik>
  );
};

export default AddLinkModal;
